import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { UploadImagem } from './upload-imagem';

const MAX = 5;

/** Como o aparelho fica montado — é o que decide o formato pedido da imagem. */
export type OrientacaoAparelho = 'retrato' | 'paisagem';

/**
 * Orientação de cada aparelho: o totem fica EM PÉ (monitor/TV vertical) e o
 * tablet fica DEITADO, preso na mesa. Imagem no formato errado entra cortada na
 * proteção de tela (o fundo é `cover`), então a instrução tem que ser por modo —
 * dizer "vertical" para quem configura o tablet fazia o gestor subir arte que
 * nunca ia caber.
 */
const FORMATO: Record<OrientacaoAparelho, { texto: string; variante: 'retrato' | 'capa' }> = {
  retrato: {
    texto:
      'Use imagens na vertical (retrato), na proporção 9:16 — a mesma de um monitor/TV em pé (ex.: 1080×1920 px). Imagens deitadas ficam cortadas.',
    variante: 'retrato',
  },
  paisagem: {
    texto:
      'Use imagens na horizontal (paisagem), na proporção 16:9 (ex.: 1920×1080 px). Imagens em pé ficam cortadas.',
    variante: 'capa',
  },
};

/**
 * Gerencia até {@link MAX} imagens de fundo da proteção de tela de um aparelho
 * (totem, totem de checkout, tablet na mesa). Reaproveita o {@link UploadImagem}
 * por slot: editar um slot troca/remove aquela imagem; o slot extra (quando há
 * espaço) adiciona uma nova.
 */
export function UploadImagensTotem({
  valor,
  onChange,
  ajuda,
  orientacao = 'retrato',
}: {
  valor: string[] | null | undefined;
  onChange: (v: string[]) => void;
  ajuda?: string;
  orientacao?: OrientacaoAparelho;
}): JSX.Element {
  const lista = valor ?? [];
  const formato = FORMATO[orientacao];

  const atualizar = (i: number, url: string | null): void => {
    const novo = [...lista];
    if (url) novo[i] = url;
    else novo.splice(i, 1); // remover slot
    onChange(novo);
  };

  const adicionar = (url: string | null): void => {
    if (url && lista.length < MAX) onChange([...lista, url]);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {ajuda ??
          `Até ${MAX} imagens que passam no fundo da proteção de tela do TOTEM (ex.: pratos e produtos). Sem imagens configuradas, é usado um fundo animado com as cores do Sischef.`}{' '}
        {formato.texto}
      </Typography>
      <Grid container spacing={2}>
        {lista.map((url, i) => (
          <Grid key={`${url}-${i}`} size={{ xs: 12, sm: 6 }}>
            <UploadImagem
              variant={formato.variante}
              finalidade="capa"
              rotulo={`Imagem ${i + 1}`}
              valor={url}
              onChange={(u) => atualizar(i, u)}
            />
          </Grid>
        ))}
        {lista.length < MAX && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <UploadImagem
              variant={formato.variante}
              finalidade="capa"
              rotulo={`Adicionar imagem (${lista.length}/${MAX})`}
              valor={null}
              onChange={adicionar}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
